import { API } from 'aws-amplify';

// CP-857: SiteList maintenance data layer.
//
// SiteList lives in the brand's UltraStaff database, not DynamoDB, so these go over the
// dqf-apiv2 REST API rather than AppSync. The 'centerlineportal' endpoint is configured in
// src/App.js and attaches the Cognito ID token automatically.
//
// Brand is implicit: REACT_APP_BASE_URL points this build at its own brand's API, which
// points at its own brand's database. There is deliberately no brand parameter — the
// Centerline portal cannot see PRST's SiteList and vice versa.
const API_NAME = 'centerlineportal';

// The API rejects invalid writes with a 400 and a user-readable message from the stored
// procedure ("Branch 820 already has a Flex row.", "LOB \"Flexx\" is not a valid line of
// business."). Surface that message rather than a generic failure, since it tells the user
// exactly what to fix.
const messageFrom = (e, fallback) => e?.response?.data?.response_message?.message || e?.message || fallback;

export const listSiteList = async () => {
  try {
    const response = await API.get(API_NAME, '/site-list');
    return response?.response_body ?? [];
  } catch (e) {
    // An empty table is a valid state (PRST's is empty until seeded) and the API returns
    // 200 with [] for it, so a 404 here means the endpoint itself is missing.
    console.log('listSiteList error', e);
    throw new Error(messageFrom(e, 'Failed to load the SiteList. Please try again later.'));
  }
};

// The LOB dropdown reuses the existing /line-of-business endpoint (the EDGE code table,
// uboT_REF_LINEOFBUSINESSCODES) — the same source the Edge Reporting criteria forms use.
// 'All' is prepended by the caller: PRST stores no LOB split, so every PRST row is 'All'.
export const listLineOfBusiness = async () => {
  try {
    const response = await API.get(API_NAME, '/line-of-business');
    return response?.response_body ?? [];
  } catch (e) {
    if (e?.message?.includes('404')) {
      return [];
    }
    console.log('listLineOfBusiness error', e);
    return [];
  }
};

export const createSiteListRow = async row => {
  try {
    const response = await API.post(API_NAME, '/site-list', { body: row });
    return response?.response_body;
  } catch (e) {
    console.log('createSiteListRow error', e);
    throw new Error(messageFrom(e, 'Failed to add the row. Please try again.'));
  }
};

// SiteList has no surrogate key, so the row is addressed by its natural key. Sending a
// different BranchNumber/LOB in the body moves the key — that is how a wrong branch number
// gets corrected — and the API duplicate-checks the destination first.
export const updateSiteListRow = async (row, orig) => {
  try {
    const response = await API.put(API_NAME, '/site-list', {
      body: { ...row, OrigBranchNumber: orig.BranchNumber, OrigLOB: orig.LOB },
    });
    return response?.response_body;
  } catch (e) {
    console.log('updateSiteListRow error', e);
    throw new Error(messageFrom(e, 'Failed to save the row. Please try again.'));
  }
};

export const deleteSiteListRow = async ({ BranchNumber, LOB }) => {
  try {
    const response = await API.del(API_NAME, '/site-list', {
      queryStringParameters: { BranchNumber, LOB },
    });
    return response?.response_body;
  } catch (e) {
    console.log('deleteSiteListRow error', e);
    throw new Error(messageFrom(e, 'Failed to delete the row. Please try again.'));
  }
};

// Bulk import. The API validates every row before writing anything, so a rejected import
// changes nothing — the per-row failures come back in response_body.errors for display.
// Throws an Error carrying a `rows` property in that case.
export const importSiteList = async ({ mode, rows }) => {
  try {
    const response = await API.post(API_NAME, '/site-list-import', {
      body: { Mode: mode, Rows: rows },
    });
    return response?.response_body;
  } catch (e) {
    console.log('importSiteList error', e);
    const error = new Error(messageFrom(e, 'The import failed. Nothing was changed.'));
    error.rows = e?.response?.data?.response_body?.errors ?? [];
    throw error;
  }
};

export const listSiteListAudit = async ({ branchNumber, lob } = {}) => {
  try {
    const queryStringParameters = {};
    if (branchNumber !== undefined && branchNumber !== null && branchNumber !== '') {
      queryStringParameters.BranchNumber = branchNumber;
    }
    if (lob) {
      queryStringParameters.LOB = lob;
    }
    const response = await API.get(API_NAME, '/site-list-audit', { queryStringParameters });
    return response?.response_body ?? [];
  } catch (e) {
    console.log('listSiteListAudit error', e);
    throw new Error(messageFrom(e, 'Failed to load the change history. Please try again later.'));
  }
};
